import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { FinancialIncomeModel } from './financialincome.model';
import { environment } from '../../../../environments/environment';
import { ResultModel } from '../../../shared/result.model';
import { ResultTotalModel } from '../../../shared/total.model';
import { ApiResponse } from '../../../models/base';

const LIST_ENDPOINT = '/financialincome';

interface ApiMessageResponse {
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class FinancialIncomeService {
  private readonly http = inject(HttpClient);

  async export(uids: string[], clientUid: string): Promise<Blob> {
    const response = await firstValueFrom(
      this.http.post(environment.apiUrl + LIST_ENDPOINT + '/export/' + clientUid, uids, {
        responseType: 'blob',
      })
    );

    return new Blob([response], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  }

  async addData(financialIncome: FinancialIncomeModel): Promise<FinancialIncomeModel> {
    const response = await firstValueFrom(
      this.http.post<ApiResponse<FinancialIncomeModel>>(
        environment.apiUrl + LIST_ENDPOINT,
        financialIncome
      )
    );

    return response.data;
  }

  async deleteData(uid: string): Promise<string> {
    const response = await firstValueFrom(
      this.http.delete<ApiMessageResponse>(environment.apiUrl + LIST_ENDPOINT + `/${uid}`)
    );

    return response.message;
  }

  async editData(
    uid: string,
    financialIncome: FinancialIncomeModel
  ): Promise<FinancialIncomeModel> {
    const response = await firstValueFrom(
      this.http.put<ApiResponse<FinancialIncomeModel>>(
        environment.apiUrl + LIST_ENDPOINT + `/${uid}`,
        financialIncome
      )
    );

    return response.data;
  }

  async loadData(uid: string): Promise<FinancialIncomeModel> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<FinancialIncomeModel>>(
        environment.apiUrl + LIST_ENDPOINT + `/${uid}`
      )
    );

    return response.data;
  }

  async getList(
    offset?: number,
    limit?: number
  ): Promise<ResultModel<FinancialIncomeModel>> {
    let params = new HttpParams();

    if (offset !== undefined) {
      params = params.set('offset', offset.toString());
    }

    if (limit !== undefined) {
      params = params.set('limit', limit.toString());
    }

    const response = await firstValueFrom(
      this.http.get<ApiResponse<ResultModel<FinancialIncomeModel>>>(
        environment.apiUrl + LIST_ENDPOINT,
        {
          params,
        }
      )
    );

    return response.data;
  }

  async getTotal(): Promise<ResultTotalModel[]> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<ResultTotalModel[]>>(
        environment.apiUrl + LIST_ENDPOINT + '/total'
      )
    );

    return response.data;
  }

  async exportAccounting(uids: string[]): Promise<Blob> {
    const response = await firstValueFrom(
      this.http.post(environment.apiUrl + LIST_ENDPOINT + '/export/excel/accounting', uids, {
        responseType: 'blob',
      })
    );

    return new Blob([response], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  }

  async exportAccountingGlobal(): Promise<Blob> {
    const response = await firstValueFrom(
      this.http.get(environment.apiUrl + LIST_ENDPOINT + '/exportGlobal/excel/accounting', {
        responseType: 'blob',
      })
    );

    return new Blob([response], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  }
}